import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowLeft, AlertTriangle, ArrowUpRight, Sparkles } from "lucide-react";
import SEO from "@/components/SEO";
import { THERAPISTS, BOOKING_URL } from "@/lib/constants";
import { track } from "@/lib/analytics";

// C2 — Symptom self-assessment.
// STRICT GUARDRAILS:
//   * No diagnosis. Service suggestion + therapist suggestion only.
//   * Red-flag check first — if any present, redirect to physician.
//   * "May help" language only.

const RED_FLAGS = [
  { key: "numbness", label: "New numbness, tingling or weakness in an arm or leg" },
  { key: "fracture", label: "Recent fracture, surgery or open wound in the area" },
  { key: "fever", label: "Fever, infection or recent unexplained weight loss" },
  { key: "headache", label: "Sudden severe headache or vision changes" },
  { key: "bleeding", label: "Bleeding disorder or recent blood clot" },
  { key: "cancer", label: "Active cancer treatment without your oncologist's clearance" },
];

const QUESTIONS = [
  {
    key: "area",
    title: "Where does it hurt most?",
    options: [
      { label: "Lower back or hips", service: "Clinical Massage Therapy", tags: ["chronic_pain", "back_neck", "lower_body"] },
      { label: "Upper back, neck or shoulders", service: "Clinical Massage Therapy", tags: ["chronic_pain", "back_neck", "neck_shoulders", "tension"] },
      { label: "Head, jaw or face", service: "Clinical Massage Therapy", tags: ["headaches", "tension"] },
      { label: "Arms, hands or wrists", service: "Clinical Massage Therapy", tags: ["neck_shoulders", "rehab"] },
      { label: "Legs, knees or feet", service: "Reflexology or Clinical Massage Therapy", tags: ["lower_body", "reflexology", "rehab"] },
      { label: "Everywhere / general tension", service: "Clinical Massage Therapy", tags: ["stress", "tension", "nervous_system"] },
    ],
  },
  {
    key: "duration",
    title: "How long has it been bothering you?",
    options: [
      { label: "Less than 2 weeks", service: null, tags: ["rehab"] },
      { label: "2–8 weeks", service: null, tags: ["rehab"] },
      { label: "Over 2 months — chronic", service: "Clinical Massage Therapy", tags: ["chronic_pain"] },
      { label: "On and off for years", service: "Clinical Massage Therapy", tags: ["chronic_pain", "tension"] },
    ],
  },
  {
    key: "context",
    title: "What seems to make it worse?",
    options: [
      { label: "Sitting at a desk", tags: ["back_neck", "tension", "headaches"] },
      { label: "Physical training or sport", tags: ["sports", "rehab"] },
      { label: "Stress, poor sleep, long days", tags: ["stress", "sleep", "nervous_system"] },
      { label: "Pregnancy / post-birth recovery", tags: ["pregnancy"] },
      { label: "Not sure", tags: [] },
    ],
  },
  {
    key: "goal",
    title: "What outcome are you hoping for?",
    options: [
      { label: "Relief from a specific pain", tags: ["chronic_pain", "rehab"] },
      { label: "Better sleep & lower stress", tags: ["sleep", "stress", "nervous_system"] },
      { label: "Get back to training / performance", tags: ["sports", "rehab"] },
      { label: "Maintenance — keep things from getting worse", tags: ["recovery", "tension"] },
    ],
  },
];

// Lifted from TherapistMatch — kept independent so each quiz can evolve.
const THERAPIST_TAGS = {
  "glenn-robinson": ["chronic_pain", "sciatica", "back_neck", "lower_body", "sports", "rehab"],
  "teshea-pitt":   ["headaches", "stress", "neck_shoulders", "pregnancy", "tension", "shiatsu"],
  "holly-paiva":   ["sleep", "reflexology", "stress", "nervous_system", "pregnancy", "recovery"],
};

function pickTherapist(tags) {
  return THERAPISTS.map((t) => {
    const profile = THERAPIST_TAGS[t.slug] || [];
    return { ...t, score: tags.filter((x) => profile.includes(x)).length };
  }).sort((a, b) => b.score - a.score);
}

export default function SymptomCheck() {
  const [stage, setStage] = useState("intro"); // intro | redflags | quiz | redirect | result
  const [flags, setFlags] = useState({});
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});

  const beginQuiz = () => {
    const hits = RED_FLAGS.filter((f) => flags[f.key]);
    if (hits.length) {
      setStage("redirect");
      track("symptom_check_redflag_redirect", { count: hits.length });
    } else {
      setStage("quiz");
      track("symptom_check_quiz_start");
    }
  };

  const q = QUESTIONS[step];
  const pick = (option) => {
    const next = { ...answers, [q.key]: option };
    setAnswers(next);
    if (step + 1 < QUESTIONS.length) {
      setStep(step + 1);
    } else {
      setStage("result");
      track("symptom_check_complete");
    }
  };

  const restart = () => {
    setStage("intro"); setFlags({}); setStep(0); setAnswers({});
  };

  return (
    <main data-testid="symptom-check-page" className="bg-white min-h-screen">
      <SEO
        title="Self check-in — Ashlan Clinic"
        description="A short questionnaire to help you describe your symptoms before booking. Not a diagnosis — a starting point."
        path="/symptom-check"
        noIndex
      />

      <section className="border-b border-[#e5e7eb] bg-white">
        <div className="max-w-3xl mx-auto px-6 md:px-12 pt-20 md:pt-28 pb-10">
          <span className="ash-eyebrow">Self Check-In</span>
          <h1 className="mt-6 text-4xl md:text-5xl lg:text-6xl font-bold leading-[1.05] tracking-tight text-[#080808]">
            A short way to describe what&apos;s going on.
          </h1>
          <div className="mt-8 border-l-4 border-[#FBBF24] bg-[#FEF3C7] p-5" data-testid="medical-disclaimer">
            <div className="flex items-start gap-3">
              <AlertTriangle size={18} className="text-[#92400E] shrink-0 mt-0.5" />
              <p className="text-sm text-[#78350F] leading-relaxed">
                <strong>This is not a medical diagnosis.</strong> Clinical Massage Therapy may help with many common
                discomforts, but it is not a substitute for medical care. If your symptoms are severe, getting worse,
                or involve any of the safety flags on the next step, please consult your physician first.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="py-12 md:py-16">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          {stage === "intro" && (
            <div className="border border-[#e5e7eb] bg-white p-8 md:p-12" data-testid="stage-intro">
              <h2 className="text-2xl font-bold text-[#080808]">Before we start — a safety check.</h2>
              <p className="mt-3 text-[#3C4048]">Tick anything that applies. If any of these are present, we&apos;ll ask you to see a physician first.</p>
              <div className="mt-6 flex flex-col gap-3">
                {RED_FLAGS.map((f) => (
                  <label key={f.key} className="flex items-start gap-3 p-3 border border-[#e5e7eb] hover:bg-[#F5F5F5] cursor-pointer" data-testid={`redflag-${f.key}`}>
                    <input
                      type="checkbox"
                      checked={!!flags[f.key]}
                      onChange={(e) => setFlags({ ...flags, [f.key]: e.target.checked })}
                      className="mt-1 w-4 h-4 accent-[#3A7A8C]"
                    />
                    <span className="text-sm text-[#3C4048] leading-relaxed">{f.label}</span>
                  </label>
                ))}
              </div>
              <button type="button" onClick={beginQuiz} data-testid="symptom-begin" className="ash-btn-primary mt-8">
                Continue <ArrowRight size={16} />
              </button>
            </div>
          )}

          {stage === "redirect" && (
            <div className="border border-[#FBBF24] bg-[#FEF3C7] p-8 md:p-12" data-testid="stage-redirect">
              <AlertTriangle size={28} className="text-[#92400E] mb-3" />
              <h2 className="text-2xl md:text-3xl font-bold text-[#080808]">Please speak to your physician first.</h2>
              <p className="mt-4 text-[#3C4048] leading-relaxed">
                Based on what you ticked, the safest next step is a medical consultation rather than a booking with us today.
                Once you have clearance, we&apos;d be glad to help — feel free to reach out with any questions in the meantime.
              </p>
              <div className="mt-8 flex flex-col sm:flex-row gap-3">
                <Link to="/contact?subject=Pre-booking%20question%20after%20self-check" className="ash-btn-primary" data-testid="redirect-contact">
                  Send us a question <ArrowRight size={16} />
                </Link>
                <button type="button" onClick={restart} className="ash-link-underline" data-testid="redirect-restart">Restart</button>
              </div>
            </div>
          )}

          {stage === "quiz" && q && (
            <div data-testid="stage-quiz" className="border border-[#e5e7eb] bg-white p-8 md:p-12">
              <div className="flex items-center justify-between mb-6">
                <span className="text-xs uppercase tracking-[0.2em] font-semibold text-[#3A7A8C]">
                  Step {step + 1} of {QUESTIONS.length}
                </span>
                <div className="h-1 flex-1 mx-6 bg-[#F5F5F5] overflow-hidden">
                  <div className="h-full bg-[#5FA8B4] transition-all"
                    style={{ width: `${((step + 1) / QUESTIONS.length) * 100}%` }} />
                </div>
              </div>
              <h2 className="text-2xl md:text-3xl font-bold text-[#080808] tracking-tight">{q.title}</h2>
              <div className="mt-8 grid gap-3">
                {q.options.map((opt) => (
                  <button
                    key={opt.label}
                    type="button"
                    onClick={() => pick(opt)}
                    data-testid={`sym-opt-${q.key}-${opt.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                    className="text-left p-5 border border-[#e5e7eb] hover:border-[#3A7A8C] hover:bg-[#F0F7F9] text-[#080808] font-semibold transition-colors flex items-center gap-3"
                  >
                    <span className="w-2 h-2 rounded-full bg-[#5FA8B4]" /> {opt.label}
                  </button>
                ))}
              </div>
              {step > 0 && (
                <button type="button" onClick={() => setStep(step - 1)} className="mt-8 ash-link-underline inline-flex items-center gap-1.5">
                  <ArrowLeft size={14} /> Back
                </button>
              )}
            </div>
          )}

          {stage === "result" && <SymptomResult answers={answers} restart={restart} />}
        </div>
      </section>
    </main>
  );
}

function SymptomResult({ answers, restart }) {
  const tags = Object.values(answers).flatMap((a) => a?.tags || []);
  const ranked = pickTherapist(tags);
  const top = ranked[0];
  const suggestedService = answers.area?.service || "Clinical Massage Therapy";

  return (
    <div data-testid="stage-result" className="border border-[#3A7A8C] bg-[#F0F7F9] p-8 md:p-12">
      <div className="flex items-center gap-2 mb-2">
        <Sparkles size={16} className="text-[#3A7A8C]" />
        <span className="ash-eyebrow">Suggested next step</span>
      </div>
      <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-[#080808] leading-tight">
        {suggestedService} <span className="text-[#3A7A8C]">may help</span> with what you described.
      </h2>
      <p className="mt-4 text-[#3C4048] leading-relaxed">
        {top.name} is the closest fit on our team for your needs. Final assignment is at the lead therapist&apos;s discretion based on availability and your clinical history.
      </p>
      <div className="mt-8 flex flex-wrap gap-3">
        <a href={top.bookingUrl} data-testid="result-book-top" className="ash-btn-primary">
          Book with {top.name.split(" ")[0]} <ArrowUpRight size={16} />
        </a>
        <a href={BOOKING_URL} className="ash-btn-secondary" data-testid="result-book-any">
          Or pick any available time <ArrowUpRight size={16} />
        </a>
        <button type="button" onClick={restart} className="ash-link-underline ml-auto" data-testid="result-restart">
          Start over
        </button>
      </div>
      <p className="mt-10 text-xs text-[#6B7280] leading-relaxed border-t border-[#3A7A8C]/30 pt-6">
        This is not a medical diagnosis — it&apos;s a starting point. If your symptoms worsen or you develop any safety
        flag (numbness, fever, sudden severe pain), please consult your physician.
      </p>
    </div>
  );
}
